/**
 * Dashboard Page
 * Main landing page after login - shows overview and statistics
 */
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useDashboardStats } from '@/hooks/useReports'
import { Users, FileText, Calendar, CheckCircle } from 'lucide-react'

export function Dashboard() {
  const { user, hasRole } = useAuth()
  const navigate = useNavigate()
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const isAdmin = hasRole('admin')

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-heading font-bold text-navy mb-2">
          Welcome back, {user?.full_name}!
        </h1>
        <p className="text-gray-600">
          You are logged in as{' '}
          <span className="font-semibold text-primary">{user?.username}</span>
          {' · '}
          <span className="text-gray-500">
            {user?.roles.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
          </span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Clients */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Total Clients
              </p>
              <p className="text-3xl font-bold text-navy mt-2">
                {statsLoading ? '...' : stats?.total_clients.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {statsLoading ? '' : `+${stats?.clients_this_month || 0} this month`}
              </p>
            </div>
            <div className="flex-shrink-0 bg-primary/10 rounded-lg p-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
          </div>
        </div>

        {/* Active Cases */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Active Cases
              </p>
              <p className="text-3xl font-bold text-navy mt-2">
                {statsLoading ? '...' : stats?.active_cases.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {statsLoading ? '' : `of ${stats?.total_cases || 0} total`}
              </p>
            </div>
            <div className="flex-shrink-0 bg-gold/10 rounded-lg p-4">
              <FileText className="w-8 h-8 text-gold" />
            </div>
          </div>
        </div>

        {/* Visits Today */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Visits This Month
              </p>
              <p className="text-3xl font-bold text-navy mt-2">
                {statsLoading ? '...' : stats?.visits_this_month.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {statsLoading ? '' : `${stats?.pending_visits || 0} pending`}
              </p>
            </div>
            <div className="flex-shrink-0 bg-accent/10 rounded-lg p-4">
              <Calendar className="w-8 h-8 text-accent" />
            </div>
          </div>
        </div>

        {/* Resolved Cases */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Total Visits
              </p>
              <p className="text-3xl font-bold text-navy mt-2">
                {statsLoading ? '...' : stats?.total_visits.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">All time</p>
            </div>
            <div className="flex-shrink-0 bg-navy/10 rounded-lg p-4">
              <CheckCircle className="w-8 h-8 text-navy" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-heading font-semibold text-navy mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/clients/new')}
            className="p-4 border-2 border-primary rounded-lg hover:bg-primary-50 transition-colors text-left"
          >
            <Users className="w-6 h-6 text-primary mb-2" />
            <p className="font-semibold text-navy">New Client</p>
            <p className="text-sm text-gray-600">Register a new client</p>
          </button>

          <button
            onClick={() => navigate('/visits/wizard')}
            className="p-4 border-2 border-gold rounded-lg hover:bg-gold-50 transition-colors text-left"
          >
            <Calendar className="w-6 h-6 text-gold mb-2" />
            <p className="font-semibold text-navy">New Visit</p>
            <p className="text-sm text-gray-600">Record a client visit</p>
          </button>

          <button
            onClick={() => navigate('/reports')}
            className="p-4 border-2 border-accent rounded-lg hover:bg-accent-50 transition-colors text-left"
          >
            <FileText className="w-6 h-6 text-accent mb-2" />
            <p className="font-semibold text-navy">View Reports</p>
            <p className="text-sm text-gray-600">Generate monthly report</p>
          </button>
        </div>
      </div>

      {/* System Status - Admins only */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-heading font-semibold text-navy mb-4">
            System Status
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Backend API:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Database:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Authentication:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                Active
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
